import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import CountUp from '../components/CountUp';
import './Talent.css';

function Talent() {
  const [openFaq, setOpenFaq] = useState(null);
  const trackRef = useRef(null);
  const marqueeRef = useRef(null);
  const animationRef = useRef(null);

  // Use requestAnimationFrame for smooth, consistent scrolling
  useEffect(() => {
    let scrollPosition = 0;
    let singleSetWidth = 0;
    
    const calculateWidth = () => {
      if (trackRef.current) {
        // Each brand item's width (including gap)
        const items = trackRef.current.children;
        if (items.length > 0) {
          const itemCount = items.length / 2; // We have 2 copies
          let totalWidth = 0;
          for (let i = 0; i < itemCount; i++) {
            totalWidth += items[i].offsetWidth;
          }
          // Add gaps (gap is between items, so itemCount - 1 gaps for one set)
          const computedStyle = window.getComputedStyle(trackRef.current);
          const gap = parseFloat(computedStyle.gap) || 48;
          totalWidth += gap * itemCount; // Include trailing gap for seamless loop
          singleSetWidth = totalWidth;
        }
      }
    };

    const animate = () => {
      if (!trackRef.current || singleSetWidth === 0) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      const isMobile = window.innerWidth <= 768;
      const speed = isMobile ? 0.75 : 0.8; // pixels per frame

      scrollPosition += speed;
      
      // Reset when we've scrolled one complete set
      if (scrollPosition >= singleSetWidth) {
        scrollPosition = 0;
      }

      trackRef.current.style.transform = `translateX(-${scrollPosition}px)`;
      animationRef.current = requestAnimationFrame(animate);
    };

    // Calculate width after images load
    const timer = setTimeout(() => {
      calculateWidth();
      animationRef.current = requestAnimationFrame(animate);
    }, 500);

    window.addEventListener('resize', calculateWidth);

    return () => {
      clearTimeout(timer);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', calculateWidth);
    };
  }, []);

  const stats = [
    { value: 135, suffix: 'm+', label: 'Follower Network' },
    { value: 70, suffix: '', label: 'Creators Represented',  },
    { value: 2, suffix: 'b+', label: 'Monthly Views' },
    { value: 10, prefix: '$', suffix: 'm+', label: 'Paid to Talent' },
    { value: 1500, suffix: '+', label: 'Campaigns', },
  ];

  const experiences = [
    { 
      title: 'Virgin Voyages', 
      desc: 'A curated creator cruise with premium dining, ocean views, and nonstop content moments.',
      image: '🚢'
    },
    { 
      title: 'F1 Las Vegas', 
      desc: 'A race weekend experience with creators on the ground for Formula 1 and exclusive access.',
      image: '🏎️'
    },
    { 
      title: 'Serie A', 
      desc: 'An annual trip to Italy with behind-the-scenes access to matchday and stadium culture.',
      image: '⚽'
    },
    { 
      title: 'Turkish Airlines', 
      desc: 'A dream trip through Türkiye — curated, filmed, and flown in style.',
      image: '✈️'
    },
  ];

  const brands = [
    { name: 'Pepsi', domain: 'pepsi.com' },
    { name: 'Hulu', domain: 'hulu.com' },
    { name: 'Heinz', domain: 'heinz.com' },
    { name: 'Kraft', domain: 'kraft.com' },
    { name: 'KFC', domain: 'kfc.com' },
    { name: 'McCormick', domain: 'mccormick.com' },
    { name: 'Virgin Voyages', domain: 'virginvoyages.com' },
    { name: "Bush's Beans", domain: 'bushbeans.com' },
    { name: '49ers', domain: '49ers.com' },
    { name: 'TCL', domain: 'tcl.com' },
  ];

  const benefits = [
    {
      title: 'Meaningful Partnerships',
      desc: 'We put creators first, cultivating genuine, long-term relationships that go beyond content and campaigns.'
    },
    {
      title: 'Collaborations That Amplify',
      desc: 'We connect you with aligned talent and opportunities to grow your reach, regardless of your current audience.'
    },
    {
      title: 'Building Your Brand Identity',
      desc: 'We leverage our experience and network to strengthen your voice, image, and lasting value as a creator.'
    },
    {
      title: 'Exclusive Campaign Access',
      desc: 'We partner closely with brands to provide creators with opportunities that are strategic, exciting, and perfectly matched.'
    },
    {
      title: 'Creative Freedom Guaranteed',
      desc: 'Your vision comes first. We ensure every brand partnership reflects your authentic style and creative voice.'
    },
    {
      title: 'Comprehensive Support',
      desc: 'From contracts to strategy, our team handles the business side so you can focus entirely on creating.'
    },
  ];

  const faqs = [
    { 
      q: 'How do I get paid?', 
      a: 'We offer direct payments via ACH, PayPal, or your preferred method — always on time, always transparent.' 
    },
    { 
      q: 'Can I work with brands outside Alooma?', 
      a: 'Yes! We\'re non-exclusive — you\'re free to pursue outside opportunities.' 
    },
    { 
      q: 'What kinds of brands will I work with?', 
      a: 'We focus on lifestyle, food, travel, wellness, and gaming — and we only pitch you for campaigns that fit your style.' 
    },
    { 
      q: 'Do I have to handle contracts or logistics?', 
      a: 'Nope. We handle all the paperwork, deadlines, and approvals — you just focus on creating.' 
    },
    { 
      q: 'What size following do I need?', 
      a: 'We work with creators at all levels. What matters most is authentic engagement and quality content.' 
    },
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="talent-page">
      {/* Hero Section */}
      <section className="creator-hero">
        <div className="creator-hero-bg">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="creator-hero-content">
          <span className="hero-label">Welcome to Alooma</span>
          <h1 className="hero-headline">
            Focus On Your Craft.<br />
            <span className="accent">We Take Care Of Everything Else.</span>
          </h1>
          <p className="hero-subtext">
            From strategic partnerships to innovative campaigns, Alooma empowers you with opportunities 
            that align with your vision, backed by a team that champions your voice at every step.
          </p>
          <Link to="/contact" className="hero-cta">
          Contact Us
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-container">
          {stats.map((stat, index) => (
            <div key={index} className="stat-item">
              <span className="stat-value">
                <CountUp 
                  end={stat.value} 
                  prefix={stat.prefix || ''} 
                  suffix={stat.suffix || ''} 
                  decimals={stat.decimals || 0}
                  duration={2500}
                />
              </span>
              <span className="stat-label">{stat.label}</span>
              <span className="stat-sub">{stat.sub}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Experiences Section - TEMPORARILY HIDDEN
      <section className="experiences-section">
        <div className="section-container">
          <span className="section-label">Experiences</span>
          <h2 className="section-headline">
            From Brand Deal to <span className="accent">Bucket List</span>.
          </h2>
          <p className="section-subtext">
            We don't just book you on brand deals — we also create once-in-a-lifetime 
            moments you'll actually want to post about.
          </p>
          <div className="experiences-grid">
            {experiences.map((exp, index) => (
              <div key={index} className="experience-card">
                <div className="experience-image">
                  <span className="experience-emoji">{exp.image}</span>
                </div>
                <div className="experience-content">
                  <h3>{exp.title}</h3>
                  <p>{exp.desc}</p>
                </div>
                <button className="experience-view">View →</button>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Brands Section */}
      <section className="brands-section">
        <div className="section-container">
          <span className="section-label">Brands</span>
          <h2 className="section-headline">
            Endorsed by the World's <span className="accent">Top Brands</span>.
          </h2>
          <p className="section-subtext">
            When the culinary/food, lifestyle, and entertainment industries need top-tier talent, 
            they come to Alooma. We connect elite creators with campaigns that deliver impact, 
            tell powerful stories, and set the standard every time.
          </p>
          <div className="brands-marquee" ref={marqueeRef}>
            <div 
              className="brands-track" 
              ref={trackRef}
            >
              {[...brands, ...brands].map((brand, index) => (
                <div key={index} className="brand-logo-item">
                  <img 
                    src={`https://img.logo.dev/${brand.domain}?token=pk_X-1ZO13GSgeOoUrIuJ6GMQ&size=400&format=png`}
                    alt={brand.name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.opacity = '0';
                    }}
                  />
                  <span className="brand-name">{brand.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="section-container">
          <h2 className="section-headline">
            Why Creators <span className="accent">Choose Alooma</span>.
          </h2>
          <p className="section-subtext">
            We provide representation that goes further, delivering an experience creators love and results that matter.
          </p>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-number">{String(index + 1).padStart(2, '0')}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section - TEMPORARILY HIDDEN
      <section className="faq-section">
        <div className="section-container">
          <span className="section-label">FAQ</span>
          <h2 className="section-headline">
            Frequently Asked <span className="accent">Questions</span>.
          </h2>
          <p className="section-subtext">
            Here's what creators usually ask before joining the network — from payments to creative control.
          </p>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className={`faq-item ${openFaq === index ? 'open' : ''}`}
                onClick={() => toggleFaq(index)}
              >
                <div className="faq-question">
                  <span>{faq.q}</span>
                  <span className="faq-toggle"></span>
                </div>
                <div className="faq-answer">
                  <div className="faq-answer-inner">
                    <p>{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <span className="section-label">Get Started</span>
          <h2 className="cta-headline">Collaborate With Us.</h2>
          <p className="cta-subtext">
          We’re seeking creators who stand out and bring unique perspectives. Get in touch, and let’s explore how we can make something great happen together.
          </p>
          <Link to="/contact" className="cta-button-large">
          Contact Us
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="creator-footer">
        <div className="footer-content">
          <p>©2025 ALOOMA. All rights reserved.</p>
          <div className="footer-socials">
            <a href="https://www.instagram.com/aloomamediagroup/" target="_blank" rel="noopener noreferrer" className="footer-social-link">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              Instagram
            </a>
            <a href="https://www.linkedin.com/company/alooma-media-group/" target="_blank" rel="noopener noreferrer" className="footer-social-link">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
              </svg>
              LinkedIn
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Talent;
